import LegalDocumentLayout, { LegalSection } from '../components/LegalDocumentLayout'

const EFFECTIVE_DATE = 'February 11, 2026'
const LAST_UPDATED = 'February 11, 2026'

const sections: LegalSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    content: (
      <>
        <p>
          This Privacy Policy explains how Asterode collects, uses, stores, and protects information when you use the app and related services.
          By using Asterode, you agree to these practices.
        </p>
        <p>
          Data Controller: Istvan Buzas (Stef Buzas). Contact:{' '}
          <a href="mailto:support@asterode.ai" className="font-semibold text-ink-500 hover:text-ink-700">
            support@asterode.ai
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: 'collection',
    title: 'Data We Collect',
    content: (
      <>
        <p>We collect information needed to operate the service and improve user experience.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Account data: email, authentication identifiers, and subscription status.</li>
          <li>Conversation data: prompts, model responses, and conversation metadata.</li>
          <li>Uploads: images, documents, and attachments you choose to send.</li>
          <li>Usage data: feature interactions, model selection patterns, and device diagnostics.</li>
          <li>Billing data: managed through third-party billing processors.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'optional-features',
    title: 'Optional Features Requiring Consent',
    content: (
      <>
        <p>Some features are off by default and only activate after explicit user consent.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            Conversation memory: allows context persistence to improve continuity between chats.
          </li>
          <li>
            App improvement participation: if enabled, may allow selected conversation data to support product improvement.
          </li>
        </ul>
        <p>You can disable these controls from within the app settings at any time.</p>
      </>
    ),
  },
  {
    id: 'usage',
    title: 'How We Use Your Data',
    content: (
      <ul className="list-disc space-y-2 pl-6">
        <li>Provide core AI chat functionality and model routing.</li>
        <li>Maintain account access, security, and service continuity.</li>
        <li>Support billing, subscription lifecycle, and entitlement checks.</li>
        <li>Detect abuse, prevent fraud, and enforce acceptable use.</li>
        <li>Improve reliability, fix bugs, and optimize performance.</li>
        <li>Comply with legal obligations where required.</li>
      </ul>
    ),
  },
  {
    id: 'ai-providers',
    title: 'Third-Party AI Providers',
    content: (
      <>
        <p>Depending on which features you use, your data is transmitted to external providers as follows.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>When you send messages (including with attachments), your data is transmitted to external AI providers (OpenAI, Anthropic, Google, Meta, etc., via OpenRouter or directly).</li>
          <li>When you use voice input, your voice data is transmitted to an external transcription service, currently OpenAI.</li>
          <li>When you use web search, your search query is transmitted to an external web search service, currently EXA.</li>
          <li>Each provider has their own terms and policies. By using Asterode, you acknowledge that your conversations will be processed by these third parties.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-Party Service Providers',
    content: (
      <>
        <p>
          Asterode relies on external providers for infrastructure, authentication, billing, and analytics. Your data may be processed by
          these providers as required to deliver requested features.
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Infrastructure: Supabase for database and authentication services.</li>
          <li>Billing: RevenueCat and platform payment processors.</li>
          <li>Application platform and diagnostics: Expo ecosystem services.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'Retention and Deletion',
    content: (
      <>
        <ul className="list-disc space-y-2 pl-6">
          <li>Conversation records are retained until deleted by you or removed through account deletion.</li>
          <li>Account data remains while your account is active.</li>
          <li>Operational logs may be retained for security and reliability monitoring.</li>
          <li>Deleted data is removed from active systems and follow-up retention windows per policy.</li>
        </ul>
        <p>
          To request full account data deletion, visit{' '}
          <a href="/app-delete" className="font-semibold text-ink-500 hover:text-ink-700">
            /app-delete
          </a>{' '}
          or email support.
        </p>
      </>
    ),
  },
  {
    id: 'rights',
    title: 'Your Controls and Rights',
    content: (
      <>
        <p>Depending on your jurisdiction, you may have rights to access, correction, deletion, portability, and objection.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Delete messages, conversations, or your full account from app controls.</li>
          <li>Disable optional memory or app improvement settings.</li>
          <li>Request data export and account-related information.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'transfers',
    title: 'International Transfers',
    content: (
      <p>
        Because Asterode uses global infrastructure providers, your data may be processed in countries other than your own. We apply commercially
        reasonable safeguards for international data handling.
      </p>
    ),
  },
  {
    id: 'security',
    title: 'Security Measures',
    content: (
      <p>
        We apply technical and organizational measures to protect data against unauthorized access, alteration, disclosure, or destruction. No
        system is perfectly secure, but we continuously improve protections as the product evolves.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    content: (
      <>
        <p>Questions or requests about privacy can be sent to:</p>
        <p>
          <a href="mailto:support@asterode.ai" className="font-semibold text-ink-500 hover:text-ink-700">
            support@asterode.ai
          </a>
        </p>
      </>
    ),
  },
]

const Privacy = () => {
  return (
    <LegalDocumentLayout
      title="Privacy Policy for Asterode"
      subtitle="Your trust is core to the product. This page explains what data we collect, why we collect it, and which controls you have."
      effectiveDate={EFFECTIVE_DATE}
      lastUpdated={LAST_UPDATED}
      introCallout={
        <p>
          Important: AI requests can involve third-party model providers. Your prompts and attachments are transmitted only as needed to generate
          responses and deliver requested features.
        </p>
      }
      sections={sections}
    />
  )
}

export default Privacy


